// The bang in the 3D look: muzzle flashes, tracers and shells that fly, sparks and dust where
// rounds land, missiles that arc out on a smoke trail, fireballs, shockwaves and a shake of
// the camera when something big goes up, and a few lights so a blast lights the ground round it.
// Everything is drawn from a handful of instanced pools (glow, smoke, streaks, chunks), so a
// heavy firefight is still only a few draw calls. Purely visual: none of it touches the sim.
// Missiles are real little bodies on a flame, laying a soft trail of smoke that spreads and
// hangs; a nuke is marked on the ground with a red dot and a shrinking countdown ring, falls
// out of the sky, whites out the screen and goes up in a mushroom cloud with a dust shockwave.
import type { IncomingStrike } from '@thrash/sim';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Brightness past white, so flashes and tracers clear the bloom threshold. */
const HOT = 5;
const GRAVITY = 9;
const MAX_GLOW = 1600;
const MAX_SMOKE = 3200;
const MAX_BODY = 96;
/** Spacing of the puffs a missile leaves behind, in tiles: close enough that the trail reads as one ribbon. */
const TRAIL_STEP = 0.06;
/** How long a nuke takes to fall from the sky onto its dot, seconds. */
const NUKE_FALL = 1.4;
const MAX_STREAK = 900;
const MAX_CHUNK = 2000;
const MAX_RINGS = 24;
/** Lights borrowed by the biggest recent flashes; a fixed count, so no shader ever recompiles. */
const LIGHTS = 3;

type Ramp = 'flat' | 'fire' | 'spark';

interface Particle {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  age: number;
  life: number;
  size0: number;
  size1: number;
  color: THREE.Color;
  ramp: Ramp;
  /** Of its speed, how much it keeps each second. */
  drag: number;
  gravity: number;
  /** Lift a puff picks up as it rises (smoke), tiles per second squared. */
  buoy: number;
  rot: number;
  spin: number;
  /** For streaks: how long it is, and whether it lies along its velocity (sparks) or a fixed direction. */
  len: number;
  /** Opacity at birth, for smoke. */
  alpha: number;
  /** Chunks only: half their size, so they sit on the ground, and whether they have stopped. */
  rest?: boolean;
  /** Chunks only: gore rather than hot wreckage. It splats where it lands instead of bouncing, and does not smoke. */
  wet?: boolean;
  /** Chunks only: spent brass casing or stone spall that bounces without trailing black wreckage smoke. */
  brass?: boolean;
  /** Chunks only: its proportions, times its size (a limb is long and thin). Absent means a rough block. */
  shape?: THREE.Vector3;
}

interface Projectile {
  from: THREE.Vector3;
  to: THREE.Vector3;
  at: number;
  dur: number;
  /** Height of the arc's top above the straight line. */
  arc: number;
  len: number;
  width: number;
  color: THREE.Color;
  /** Puffs of smoke left behind, one every this many ms (0: none). */
  trail: number;
  lastTrail: number;
  head: number;
  /** A missile: drawn as a body on a flame, speeding up as it goes, laying a continuous trail. */
  missile?: boolean;
  /** Where the trail has been laid up to. */
  trailAt?: THREE.Vector3;
  hit: () => void;
}

/** A nuke or EMP called in: its marks on the ground, and the warhead on its way down. */
interface Marker {
  group: THREE.Group;
  ring: THREE.Mesh;
  count: THREE.Mesh;
  dot: THREE.Mesh;
  ticks: THREE.Mesh;
  kind: IncomingStrike['kind'];
  pos: THREE.Vector3;
  radius: number;
  /** Seconds left when first seen, for the countdown ring. */
  total: number;
  /** When it is expected to land, smoothed from the sim's countdown. */
  landAt: number;
  seen: number;
  warhead?: THREE.Vector3;
  /** The warhead rides in on a unit drawn on its own arc: no second one falls onto the mark. */
  carried: boolean;
}

/** A nuke going up: its cloud is built over several seconds. */
interface Nuke { pos: THREE.Vector3; at: number; r: number; acc: Record<string, number> }

interface Ring { mesh: THREE.Mesh; at: number; life: number; r0: number; r1: number; color: THREE.Color }

interface Flash { pos: THREE.Vector3; at: number; life: number; power: number; color: THREE.Color }

/** A soft round dot: bright middle, smooth edge. */
function dotTexture(): THREE.CanvasTexture {
  const n = 64;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.25, 'rgba(255,255,255,0.8)');
  grad.addColorStop(0.6, 'rgba(255,255,255,0.22)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, n, n);
  return new THREE.CanvasTexture(c);
}

/**
 * A billowing puff: fractal noise eats into a soft round edge, so each puff has a ragged,
 * cauliflower outline and some inner texture rather than reading as a disc or a ball.
 */
function puffTexture(): THREE.CanvasTexture {
  const n = 128;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const img = g.createImageData(n, n);
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const G = 16;
  const lattice = Array.from({ length: G * G }, rnd);
  const at = (x: number, y: number) => lattice[(((y % G) + G) % G) * G + (((x % G) + G) % G)]!;
  const noise = (x: number, y: number) => {
    const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
    const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
    const a = at(xi, yi) + (at(xi + 1, yi) - at(xi, yi)) * u;
    const b = at(xi, yi + 1) + (at(xi + 1, yi + 1) - at(xi, yi + 1)) * u;
    return a + (b - a) * v;
  };
  const smooth = (e0: number, e1: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
    return t * t * (3 - 2 * t);
  };
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      let f = 0, amp = 0.5, freq = 4;
      for (let o = 0; o < 4; o++) { f += amp * noise((x / n) * freq, (y / n) * freq); amp *= 0.5; freq *= 2; }
      const d = Math.hypot(x - n / 2, y - n / 2) / (n / 2);
      const edge = 1 - smooth(0.3, 1, d + (f - 0.47) * 0.8);
      const a = Math.min(1, Math.max(0, edge * (0.45 + f * 0.8)));
      const k = (y * n + x) * 4;
      const shade = 255 * (0.8 + 0.2 * f);
      img.data[k] = img.data[k + 1] = img.data[k + 2] = shade;
      img.data[k + 3] = a * 255;
    }
  }
  g.putImageData(img, 0, 0);
  return new THREE.CanvasTexture(c);
}

/** A dark smudge, darkest in the middle: the ground a nuke has burnt. */
function scorchTexture(): THREE.CanvasTexture {
  const n = 128;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
  grad.addColorStop(0, 'rgba(10,8,6,0.95)');
  grad.addColorStop(0.55, 'rgba(25,20,15,0.75)');
  grad.addColorStop(1, 'rgba(30,25,20,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, n, n);
  return new THREE.CanvasTexture(c);
}

/** A small missile along +X, nose forward: body, nose cone and four tail fins, in tiles. */
function missileGeometry(): THREE.BufferGeometry {
  const r = 0.028, len = 0.3;
  const parts: THREE.BufferGeometry[] = [
    new THREE.CylinderGeometry(r, r, len, 10).rotateZ(-Math.PI / 2),
    new THREE.ConeGeometry(r, 0.1, 10).rotateZ(-Math.PI / 2).translate(len / 2 + 0.05, 0, 0),
  ];
  for (let k = 0; k < 4; k++) parts.push(new THREE.BoxGeometry(0.08, 0.005, 0.055).translate(-len / 2 + 0.045, 0, r + 0.022).rotateX((k * Math.PI) / 2));
  return mergeGeometries(parts)!;
}

/** Basic material with a per-instance opacity, for smoke that thins as it spreads. */
function fadingMaterial(params: THREE.MeshBasicMaterialParameters): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial(params);
  m.onBeforeCompile = shader => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aAlpha;\nvarying float vAlpha;\nvarying float vShade;')
      // which way this corner of the card lies on screen, so the puff is lit from above and shaded beneath whatever its spin
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvAlpha = aAlpha;\nvShade = normalize((viewMatrix * modelMatrix * instanceMatrix * vec4(position, 0.0)).xyz).y;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying float vAlpha;\nvarying float vShade;')
      .replace('#include <alphamap_fragment>', '#include <alphamap_fragment>\ndiffuseColor.a *= vAlpha;\ndiffuseColor.rgb *= mix(0.66, 1.12, clamp(vShade * 0.75 + 0.5, 0.0, 1.0));');
  };
  return m;
}

/** One instanced mesh, refilled from scratch every frame. */
class Pool {
  readonly mesh: THREE.InstancedMesh;
  private readonly alpha: THREE.InstancedBufferAttribute | null;
  private n = 0;
  constructor(geo: THREE.BufferGeometry, mat: THREE.Material, readonly max: number, withAlpha = false) {
    if (withAlpha) {
      this.alpha = new THREE.InstancedBufferAttribute(new Float32Array(max), 1);
      geo.setAttribute('aAlpha', this.alpha);
    } else this.alpha = null;
    this.mesh = new THREE.InstancedMesh(geo, mat, max);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.setColorAt(0, new THREE.Color());
    this.mesh.instanceColor!.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
  }
  get full(): boolean { return this.n >= this.max; }
  begin(): void { this.n = 0; }
  push(m: THREE.Matrix4, c: THREE.Color, a = 1): void {
    if (this.n >= this.max) return;
    this.mesh.setMatrixAt(this.n, m);
    this.mesh.setColorAt(this.n, c);
    if (this.alpha) this.alpha.setX(this.n, a);
    this.n++;
  }
  /**
   * A camera-facing card, written straight into the buffers: `right`, `up` and `fwd` are the
   * camera's axes, the card turned by `rot` within them. Much cheaper than composing a matrix.
   */
  pushCard(pos: THREE.Vector3, size: number, rot: number, right: THREE.Vector3, up: THREE.Vector3, fwd: THREE.Vector3, c: THREE.Color, a = 1): void {
    if (this.n >= this.max) return;
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const o = this.n * 16;
    const cs = Math.cos(rot) * size, sn = Math.sin(rot) * size;
    m[o] = right.x * cs + up.x * sn; m[o + 1] = right.y * cs + up.y * sn; m[o + 2] = right.z * cs + up.z * sn; m[o + 3] = 0;
    m[o + 4] = up.x * cs - right.x * sn; m[o + 5] = up.y * cs - right.y * sn; m[o + 6] = up.z * cs - right.z * sn; m[o + 7] = 0;
    m[o + 8] = fwd.x * size; m[o + 9] = fwd.y * size; m[o + 10] = fwd.z * size; m[o + 11] = 0;
    m[o + 12] = pos.x; m[o + 13] = pos.y; m[o + 14] = pos.z; m[o + 15] = 1;
    const col = this.mesh.instanceColor!.array as Float32Array;
    col[this.n * 3] = c.r; col[this.n * 3 + 1] = c.g; col[this.n * 3 + 2] = c.b;
    if (this.alpha) (this.alpha.array as Float32Array)[this.n] = a;
    this.n++;
  }
  end(): void {
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    if (this.alpha) this.alpha.needsUpdate = true;
  }
}

const tmpM = new THREE.Matrix4();
const tmpQ = new THREE.Quaternion();
const tmpQ2 = new THREE.Quaternion();
const tmpS = new THREE.Vector3();
const tmpV = new THREE.Vector3();
const tmpC = new THREE.Color();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const FIRE_HOT = new THREE.Color(1, 0.92, 0.7);
const FIRE_MID = new THREE.Color(1, 0.5, 0.12);
const FIRE_COOL = new THREE.Color(0.5, 0.12, 0.03);

/** How big a muzzle flash, tracer and hit each kind of gun gets. */
export type Calibre = 'small' | 'heavy' | 'cannon' | 'artillery' | 'sniper';

/** The camera shake is off unless the player turned it on in Settings, and the choice is remembered. */
const SHAKE_KEY = 'thrash.shake';
let shakeOn = ((): boolean => {
  try { return localStorage.getItem(SHAKE_KEY) === '1'; } catch { return false; }
})();

/** Whether big blasts shake the view. */
export function isShakeOn(): boolean {
  return shakeOn;
}

export function setShakeOn(on: boolean): void {
  shakeOn = on;
  try { localStorage.setItem(SHAKE_KEY, on ? '1' : '0'); } catch { /* storage unavailable: it lasts this visit */ }
}

export class CombatFx3D {
  readonly group = new THREE.Group();
  /** The blast lights, kept apart from the rest so a pass that hides the effects still has the same lights. */
  readonly lightGroup = new THREE.Group();
  private readonly glow: Pool;
  private readonly smoke: Pool;
  private readonly streak: Pool;
  private readonly chunk: Pool;
  /** Gore, drawn in its own shapes: round drops (flat stains once they land), lumps of flesh, lengths of gut. */
  private readonly drop: Pool;
  private readonly gib: Pool;
  private readonly gut: Pool;
  private readonly body: Pool;
  private readonly markers = new Map<string, Marker>();
  private readonly markerGeo = {
    ring: new THREE.RingGeometry(0.97, 1, 96).rotateX(-Math.PI / 2),
    count: new THREE.RingGeometry(0.9, 1, 96).rotateX(-Math.PI / 2),
    dot: new THREE.CircleGeometry(1, 24).rotateX(-Math.PI / 2),
    // four short marks round the rim, like a gun sight
    ticks: mergeGeometries([0, 1, 2, 3].map(k => new THREE.PlaneGeometry(0.4, 0.035).rotateX(-Math.PI / 2).translate(0.95, 0, 0).rotateY((k * Math.PI) / 2)))!,
  };
  private readonly exhausts = new Map<number, { at: THREE.Vector3; seen: number }>();
  private nukes: Nuke[] = [];
  private readonly scorches: { mesh: THREE.Mesh; at: number }[] = [];
  private readonly dome: THREE.Mesh;
  private domeAt = -Infinity;
  private domeR = 1;
  private white = { at: -Infinity, life: 1 };
  private quake = { at: -Infinity, life: 1, amp: 0 };
  private glows: Particle[] = [];
  private smokes: Particle[] = [];
  private sparks: Particle[] = [];
  private chunks: Particle[] = [];
  private shots: Projectile[] = [];
  /** Lasers held on their targets, by emitter: where from, where to, and until when it stays lit without a fresh shot. */
  private readonly beams = new Map<string, { from: THREE.Vector3; to: THREE.Vector3; until: number; lit: number; miss: boolean }>();
  private rings: Ring[] = [];
  private flashes: Flash[] = [];
  private readonly ringPool: THREE.Mesh[] = [];
  private readonly lights: THREE.PointLight[] = [];
  private last = 0;
  private trauma = 0;
  private readonly camQ = new THREE.Quaternion();
  private readonly camRight = new THREE.Vector3();
  private readonly camUp = new THREE.Vector3();
  private readonly camFwd = new THREE.Vector3();
  private seed = 1;
  private evict = 0;

  constructor() {
    const quad = new THREE.PlaneGeometry(1, 1);
    this.glow = new Pool(quad, new THREE.MeshBasicMaterial({ map: dotTexture(), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }), MAX_GLOW);
    this.smoke = new Pool(new THREE.PlaneGeometry(1, 1), fadingMaterial({ map: puffTexture(), transparent: true, depthWrite: false }), MAX_SMOKE, true);
    // a streak is a thin box one unit long, its tail at the origin, so it scales out along +X
    const bar = new THREE.BoxGeometry(1, 1, 1).translate(-0.5, 0, 0);
    this.streak = new Pool(bar, new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }), MAX_STREAK);
    this.chunk = new Pool(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshLambertMaterial({ color: 0xffffff }), MAX_CHUNK);
    this.chunk.mesh.castShadow = true;
    // wet, glossy and dark: blood and flesh catch the light the way the dry wreckage does not
    const wet = (rough: number) => new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: rough, metalness: 0.05 });
    this.drop = new Pool(new THREE.SphereGeometry(0.5, 8, 6), wet(0.25), MAX_CHUNK);
    const lump = new THREE.IcosahedronGeometry(0.6, 1);
    {
      // knock every vertex in or out a little, so no two look like the same sphere
      const pos = lump.getAttribute('position') as THREE.BufferAttribute;
      const moved = new Map<string, number>();
      let seed = 7;
      for (let i = 0; i < pos.count; i++) {
        const key = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
        let k = moved.get(key);
        if (k === undefined) { seed = (seed * 16807) % 2147483647; k = 0.75 + (seed / 2147483647) * 0.5; moved.set(key, k); }
        pos.setXYZ(i, pos.getX(i) * k, pos.getY(i) * k, pos.getZ(i) * k);
      }
      lump.computeVertexNormals();
    }
    this.gib = new Pool(lump, wet(0.45), MAX_CHUNK);
    this.gut = new Pool(new THREE.CapsuleGeometry(0.35, 1, 3, 8), wet(0.3), MAX_CHUNK);
    for (const pool of [this.drop, this.gib, this.gut]) { pool.mesh.castShadow = true; this.group.add(pool.mesh); }
    this.body = new Pool(missileGeometry(), new THREE.MeshStandardMaterial({ color: 0xdcdcd4, metalness: 0.35, roughness: 0.45 }), MAX_BODY);
    this.body.mesh.castShadow = true;
    this.group.add(this.body.mesh);
    const scorch = new THREE.MeshBasicMaterial({ map: scorchTexture(), transparent: true, depthWrite: false });
    for (let i = 0; i < 4; i++) {
      const mesh = new THREE.Mesh(this.markerGeo.dot, scorch.clone());
      mesh.visible = false;
      this.scorches.push({ mesh, at: -Infinity });
      this.group.add(mesh);
    }
    // the white shell of air a nuke shoves out in its first instant
    this.dome = new THREE.Mesh(new THREE.SphereGeometry(1, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2), new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, side: THREE.DoubleSide }));
    this.dome.visible = false;
    this.dome.renderOrder = 2;
    this.group.add(this.dome);
    // glow and streaks draw after the smoke, so a fireball shows through its own cloud
    this.smoke.mesh.renderOrder = 1;
    this.glow.mesh.renderOrder = 2;
    this.streak.mesh.renderOrder = 2;
    this.group.add(this.chunk.mesh, this.smoke.mesh, this.glow.mesh, this.streak.mesh);
    const ringGeo = new THREE.RingGeometry(0.95, 1, 64).rotateX(-Math.PI / 2);
    for (let i = 0; i < MAX_RINGS; i++) {
      const mesh = new THREE.Mesh(ringGeo, new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
      mesh.visible = false;
      mesh.renderOrder = 2;
      this.ringPool.push(mesh);
      this.group.add(mesh);
    }
    for (let i = 0; i < LIGHTS; i++) {
      const l = new THREE.PointLight(0xffa050, 0, 4, 1.6);
      this.lights.push(l);
      this.lightGroup.add(l);
    }
  }

  private rnd(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return this.seed / 2147483647;
  }

  /** A random direction, flattened towards the ground by `flat` (0 a sphere, 1 a disc). */
  private dir(flat = 0, up = 0): THREE.Vector3 {
    const a = this.rnd() * Math.PI * 2;
    const y = (this.rnd() * 2 - 1) * (1 - flat) + up;
    const r = Math.sqrt(Math.max(0, 1 - Math.min(1, y * y)));
    return new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r).normalize();
  }

  private particle(list: Particle[], max: number, p: Partial<Particle> & { pos: THREE.Vector3; life: number }): void {
    const fresh: Particle = {
      vel: new THREE.Vector3(), age: 0, size0: 0.2, size1: 0.2, color: new THREE.Color(1, 1, 1), ramp: 'flat',
      drag: 1, gravity: 0, buoy: 0, rot: this.rnd() * 6.28, spin: (this.rnd() - 0.5) * 2, len: 0.1, alpha: 1, ...p,
    };
    // a full pool takes the new one in place of an old one, rather than shifting the whole list along
    if (list.length >= max) list[(this.evict = (this.evict + 1) % max)] = fresh;
    else list.push(fresh);
  }

  private glowAt(pos: THREE.Vector3, life: number, size0: number, size1: number, color: THREE.Color, ramp: Ramp = 'flat', vel = new THREE.Vector3()): void {
    this.particle(this.glows, MAX_GLOW, { pos: pos.clone(), vel, life, size0, size1, color, ramp, drag: 0.08 });
  }

  /** A puff of smoke; `shade` is its grey as it would be picked on screen (0 black, 1 white). */
  private smokeAt(pos: THREE.Vector3, life: number, size0: number, size1: number, shade: number, alpha: number, vel = new THREE.Vector3(), buoy = 0.4): void {
    this.particle(this.smokes, MAX_SMOKE, { pos: pos.clone(), vel, life, size0, size1, color: new THREE.Color(shade, shade * 0.97, shade * 0.94).convertSRGBToLinear(), alpha, drag: 0.3, buoy });
  }

  /** A puff in any colour (picked as on screen), slower to lose its speed: the big clouds. */
  private cloudAt(pos: THREE.Vector3, life: number, size0: number, size1: number, r: number, g: number, b: number, alpha: number, vel: THREE.Vector3, buoy: number, drag = 0.5): void {
    this.particle(this.smokes, MAX_SMOKE, { pos: pos.clone(), vel, life, size0, size1, color: new THREE.Color(r, g, b).convertSRGBToLinear(), alpha, drag, buoy });
  }

  /** Lays trail smoke from `from` towards `to` at an even spacing, moving `from` up to the last puff laid. */
  private trailPuffs(from: THREE.Vector3, to: THREE.Vector3, size = 1, linger = 1): void {
    const d = from.distanceTo(to);
    const n = Math.floor(d / TRAIL_STEP);
    if (n <= 0) return;
    const step = tmpV.copy(to).sub(from).multiplyScalar(TRAIL_STEP / d).clone();
    for (let i = 0; i < n; i++) {
      from.add(step);
      // starts tight and white-hot behind the nozzle, spreads into a soft grey ribbon that hangs and drifts
      this.smokeAt(from, (2400 + this.rnd() * 1800) * linger, 0.05 * size, (0.38 + this.rnd() * 0.3) * size, 0.86 + this.rnd() * 0.08, 0.42, this.dir().multiplyScalar(0.06), 0.07);
    }
  }

  /** The flame and smoke out of the tail of a flying missile the scene draws itself (a cruise missile unit). */
  exhaust(key: number, tail: THREE.Vector3, dir: THREE.Vector3): void {
    let e = this.exhausts.get(key);
    if (!e) { e = { at: tail.clone(), seen: this.last }; this.exhausts.set(key, e); }
    e.seen = this.last;
    this.trailPuffs(e.at, tail, 1.3);
    const flicker = 0.8 + 0.4 * this.rnd();
    this.glowAt(tail, 40, 0.22 * flicker, 0.1, new THREE.Color(1, 0.7, 0.35).multiplyScalar(HOT));
    this.glowAt(tail.clone().addScaledVector(dir, -0.08), 60, 0.14, 0.05, new THREE.Color(1, 0.45, 0.15).multiplyScalar(HOT * 0.6));
  }

  /** A launcher firing: a flash and a billow of smoke rolling out round it. */
  launch(pos: THREE.Vector3, size: number): void {
    this.glowAt(pos.clone().setY(pos.y + 0.2), 120, 0.4 * size, 0.9 * size, new THREE.Color(1, 0.85, 0.6).multiplyScalar(HOT));
    for (let k = 0; k < 10; k++) this.smokeAt(pos.clone().setY(pos.y + 0.1), 2200 + this.rnd() * 1500, 0.2 * size, (0.6 + this.rnd() * 0.5) * size, 0.85, 0.55, this.dir(0.75, 0.15).multiplyScalar(1.2 * size), 0.15);
    this.light(pos, 2, 160);
  }

  private sparkAt(pos: THREE.Vector3, vel: THREE.Vector3, life: number, len: number, color: THREE.Color, gravity = GRAVITY): void {
    this.particle(this.sparks, MAX_STREAK, { pos: pos.clone(), vel, life, len, size0: 0.018, size1: 0.01, color, ramp: 'spark', drag: 0.25, gravity });
  }

  /** Something bright for a moment: lights the ground round it if it is among the biggest just now. */
  private light(pos: THREE.Vector3, power: number, life: number, color = 0xffa050): void {
    this.flashes.push({ pos: pos.clone(), at: this.last, life, power, color: new THREE.Color(color) });
  }

  private ring(pos: THREE.Vector3, r0: number, r1: number, life: number, color: THREE.Color): void {
    const mesh = this.ringPool.find(m => !m.visible) ?? this.ringPool[0]!;
    this.rings = this.rings.filter(r => r.mesh !== mesh);
    mesh.position.copy(pos).setY(pos.y + 0.03);
    mesh.visible = true;
    this.rings.push({ mesh, at: this.last, life, r0, r1, color });
  }

  /** Kicks the camera; big blasts add up, capped so it never goes seasick. */
  shakeBy(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  /** The camera's offset this frame, in world units along the screen's right and up. */
  shake(now: number): [number, number] {
    if (!shakeOn) return [0, 0];
    const s = this.trauma * this.trauma * 0.35;
    const t = now / 1000;
    let x = s * Math.sin(t * 61) * Math.cos(t * 23), y = s * Math.sin(t * 53 + 1) * Math.cos(t * 31);
    // a nuke's long, heavy rumble on top: slower, bigger swings that die away over seconds
    const q = (now - this.quake.at) / this.quake.life;
    if (q >= 0 && q < 1) {
      const a = this.quake.amp * 0.6 * (1 - q) * (1 - q);
      x += a * Math.sin(t * 19) * Math.cos(t * 7.3);
      y += a * Math.sin(t * 17 + 2) * Math.cos(t * 5.1);
    }
    return [x, y];
  }

  /** A gun going off at `pos`, pointing along `dir` (flat on the ground plane). */
  muzzle(pos: THREE.Vector3, dir: THREE.Vector3, calibre: Calibre): void {
    const big = calibre === 'artillery' ? 3.8 : calibre === 'cannon' ? 3.2 : calibre === 'heavy' ? 1.6 : calibre === 'sniper' ? 1.5 : 1;
    const gunDir = calibre === 'artillery' ? dir.clone().setY(0.48).normalize() : dir;
    const hot = new THREE.Color(1, 0.88, 0.58).multiplyScalar(HOT * 1.15);
    const orange = new THREE.Color(1, 0.62, 0.22).multiplyScalar(HOT);
    this.glowAt(pos, 70 * Math.sqrt(big), 0.24 * big, 0.12 * big, hot);
    // the flame thrown forward out of the barrel
    const fwd = pos.clone().addScaledVector(gunDir, 0.09 * big);
    this.glowAt(fwd, 55 * Math.sqrt(big), 0.16 * big, 0.34 * big, orange, 'flat', gunDir.clone().multiplyScalar(2.8 * big));
    // 3D directional muzzle-brake flash spikes: a long forward jet and two angled side-vent jets
    const right = new THREE.Vector3(-dir.z, 0, dir.x);
    if (right.lengthSq() > 1e-6) right.normalize(); else right.set(0, 0, 1);
    this.sparkAt(pos, gunDir.clone().multiplyScalar(6.5 * Math.sqrt(big)), 55, 0.13 * big, hot, 0);
    if (calibre !== 'small') {
      for (const s of [-1, 1]) {
        const brakeDir = gunDir.clone().multiplyScalar(calibre === 'artillery' ? 0.25 : 0.45).addScaledVector(right, s * (calibre === 'artillery' ? 1.15 : 0.9)).normalize();
        this.sparkAt(pos, brakeDir.multiplyScalar(4.2 * Math.sqrt(big)), 55, 0.1 * big, orange, 0);
      }
    }
    for (let k = 0; k < (calibre === 'small' ? 3 : calibre === 'artillery' ? 9 : 6); k++) {
      const v = gunDir.clone().multiplyScalar(3.2 + this.rnd() * 3.5).addScaledVector(this.dir(0.3), 1.5).multiplyScalar(Math.sqrt(big));
      this.sparkAt(pos, v, 90 + this.rnd() * 80, 0.065, new THREE.Color(1, 0.75, 0.35).multiplyScalar(HOT), 2);
    }
    // spent brass shell casing kicked out of the breech to the right
    if (calibre !== 'cannon') {
      const s = calibre === 'artillery' ? 0.036 : calibre === 'heavy' ? 0.022 : calibre === 'sniper' ? 0.019 : 0.015;
      const eject = right.clone().multiplyScalar(0.9 + this.rnd() * 0.6)
        .addScaledVector(dir, (calibre === 'artillery' ? -0.9 : -0.25) + (this.rnd() - 0.5) * 0.2)
        .setY(1.2 + this.rnd() * 0.7);
      this.particle(this.chunks, MAX_CHUNK, {
        pos: pos.clone().addScaledVector(gunDir, calibre === 'artillery' ? -0.95 : -0.06), vel: eject, life: 1600 + this.rnd() * 700,
        size0: s, size1: s, color: new THREE.Color(0.88, 0.68, 0.24), gravity: GRAVITY * 1.15, drag: 0.75,
        spin: (this.rnd() < 0.5 ? 1 : -1) * (24 + this.rnd() * 14), len: s, brass: true, shape: new THREE.Vector3(2.4, 0.65, 0.65),
      });
    }
    if (calibre !== 'small') {
      const n = calibre === 'artillery' ? 12 : calibre === 'cannon' ? 8 : 3;
      for (let k = 0; k < n; k++) {
        const v = gunDir.clone().multiplyScalar((0.4 + this.rnd()) * big * 0.6).addScaledVector(this.dir(0.4), 0.3 * big);
        this.smokeAt(fwd, 900 + this.rnd() * 700, 0.1 * big, 0.45 * big, 0.78, 0.48, v, 0.25);
      }
    }
    if (calibre === 'cannon' || calibre === 'artillery') {
      // tank / howitzer main gun overpressure wave: ground shockwave ring and lateral dust blasted out beneath the muzzle brake
      this.ring(pos.clone().setY(0), 0.15, calibre === 'artillery' ? 1.75 : 1.35, 240, new THREE.Color(1, 0.78, 0.45).multiplyScalar(HOT * 0.16));
      for (const s of [-1, 1]) {
        const dustVel = right.clone().multiplyScalar(s * (1.5 + this.rnd() * 0.9)).addScaledVector(dir, 0.5);
        this.smokeAt(pos.clone().setY(0.04), 900 + this.rnd() * 450, 0.18, 0.72, 0.52, 0.58, dustVel, 0.05);
      }
      this.light(pos, calibre === 'artillery' ? 4.2 : 3.4, 150);
      this.shakeBy(calibre === 'artillery' ? 0.17 : 0.13);
    } else if (calibre !== 'small') {
      this.light(pos, 1.0, 75);
    }
  }

  /** A round from `from` to `to`: a hot dash that races there, then hits. */
  /** A round flying from `from` to `to`; one that `miss`es just flies on out of sight with no strike at the end. */
  bullet(from: THREE.Vector3, to: THREE.Vector3, calibre: Calibre, now: number, miss = false, round?: 'incendiary' | 'explosive'): void {
    const d = from.distanceTo(to);
    const speed = calibre === 'sniper' ? 62 : calibre === 'artillery' ? 20 : calibre === 'cannon' ? 30 : 40;
    // special rounds burn their own colour: incendiary orange, explosive red
    const color = round === 'explosive' ? new THREE.Color(1, 0.22, 0.12) : round === 'incendiary' ? new THREE.Color(1, 0.5, 0.1) : calibre === 'sniper' ? new THREE.Color(0.75, 0.92, 1) : calibre === 'artillery' ? new THREE.Color(1, 0.68, 0.28) : calibre === 'cannon' ? new THREE.Color(1, 0.72, 0.32) : new THREE.Color(1, 0.8, 0.42);
    this.shots.push({
      from: from.clone(), to: to.clone(), at: now, dur: Math.max(30, (d / speed) * 1000),
      arc: calibre === 'artillery' ? Math.max(1.4, d * 0.24) : calibre === 'cannon' ? d * 0.028 : 0,
      len: calibre === 'sniper' ? 1.55 : calibre === 'artillery' ? 0.72 : calibre === 'cannon' ? 0.62 : 0.48,
      width: (calibre === 'artillery' ? 0.09 : calibre === 'cannon' ? 0.075 : calibre === 'heavy' ? 0.044 : 0.03) * (round ? 1.35 : 1),
      color: color.multiplyScalar(HOT), trail: calibre === 'artillery' ? 15 : calibre === 'cannon' ? 20 : calibre === 'sniper' ? 35 : 0,
      lastTrail: now, head: calibre === 'artillery' ? 0.24 : calibre === 'cannon' ? 0.18 : calibre === 'heavy' ? 0.07 : 0,
      hit: () => { if (!miss) { if (calibre === 'artillery') this.blast(to, 1.35); else if (calibre === 'cannon') this.blast(to, 0.58); else this.impact(to, calibre); } },
    });
  }

  /** A missile from `from` to `to`: it leaves the launcher in a billow of smoke, climbs and speeds up on its flame, and bursts where it lands, or in the air for an interception. */
  missile(from: THREE.Vector3, to: THREE.Vector3, dur: number, now: number, air = false): void {
    const d = from.distanceTo(to);
    this.launch(from, 0.6);
    // recoilless / ATGM backblast cone vented out behind ground and shoulder-fired launchers
    if (from.y < 0.75 && d > 1e-3) {
      const back = from.clone().sub(to).setY(0);
      if (back.lengthSq() > 1e-6) {
        back.normalize();
        const vent = from.clone().addScaledVector(back, 0.14);
        this.glowAt(vent, 95, 0.22, 0.42, new THREE.Color(1, 0.75, 0.4).multiplyScalar(HOT * 0.9), 'fire', back.clone().multiplyScalar(2.4));
        for (let k = 0; k < 6; k++) {
          const v = back.clone().multiplyScalar(1.6 + this.rnd() * 1.8).addScaledVector(this.dir(0.5, 0.15), 0.65);
          this.smokeAt(vent, 1300 + this.rnd() * 800, 0.12, 0.58, 0.82, 0.6, v, 0.18);
        }
      }
    }
    this.shots.push({
      from: from.clone(), to: to.clone(), at: now, dur: dur * 0.95, arc: air ? d * 0.15 : d * 0.28, len: 0, width: 0,
      color: new THREE.Color(1, 0.8, 0.5).multiplyScalar(HOT), trail: 0, lastTrail: now, head: 0, missile: true, trailAt: from.clone(),
      hit: () => (air ? this.airBurst(to) : this.blast(to, 0.8)),
    });
  }

  /** A round landing: a spit of sparks, ricochet streaks and a kick of dust. */
  impact(pos: THREE.Vector3, calibre: Calibre): void {
    const big = calibre === 'heavy' ? 1.55 : calibre === 'sniper' ? 1.9 : 1;
    this.glowAt(pos, 65, 0.15 * big, 0.05, new THREE.Color(1, 0.82, 0.52).multiplyScalar(HOT * 0.9));
    const n = calibre === 'small' ? 5 : 9;
    for (let k = 0; k < n; k++) this.sparkAt(pos, this.dir(0.2, 0.5).multiplyScalar(2.2 + this.rnd() * 3.2 * big), 150 + this.rnd() * 200, 0.075, new THREE.Color(1, 0.72, 0.32).multiplyScalar(HOT));
    // high-speed ricochet tracer skipping off armour or stone
    if (calibre !== 'small' || this.rnd() < 0.45) {
      const ric = this.dir(0.35, 0.55).multiplyScalar((6 + this.rnd() * 5) * Math.sqrt(big));
      this.sparkAt(pos, ric, 240 + this.rnd() * 180, 0.15 * big, new THREE.Color(1, 0.88, 0.6).multiplyScalar(HOT * 1.15), GRAVITY * 0.6);
    }
    if (pos.y < 0.35) {
      for (let k = 0; k < (calibre === 'small' ? 2 : 4); k++) this.smokeAt(pos, 650 + this.rnd() * 500, 0.07 * big, 0.34 * big, 0.62, 0.55, this.dir(0.5, 0.6).multiplyScalar(0.65), 0.1);
    }
  }

  /** A shell or missile going off on the ground: fireball, sparks, dirt, a ring, a shove of the camera. */
  blast(pos: THREE.Vector3, size: number): void {
    this.fireball(pos, size);
    this.shakeBy(0.18 * size);
  }

  /**
   * A fire mission landing: a battery's worth of 155 mm shells coming down out of the sky on
   * bright streaks over a second and a half, scattered across the circle `r` tiles round `centre`.
   * Each one throws a fountain of earth, a fireball and a ring across the ground; a low grey pall
   * hangs over the whole beaten zone afterwards.
   */
  barrage(centre: THREE.Vector3, r: number): void {
    const now = this.last;
    const shells = 9;
    for (let k = 0; k < shells; k++) {
      // the first lands near the middle, the rest walk out over the circle
      const a = this.rnd() * Math.PI * 2, d = k === 0 ? 0 : r * (0.25 + 0.75 * Math.sqrt(this.rnd()));
      const to = centre.clone().add(new THREE.Vector3(Math.cos(a) * d, 0, Math.sin(a) * d)).setY(0.05);
      // in from high up and a little to one side, the way the guns are
      const from = to.clone().add(new THREE.Vector3(-3 + this.rnd() * 1.5, 16, -2 + this.rnd() * 1.5));
      this.shots.push({
        from, to, at: now + k * 160 + this.rnd() * 90, dur: 420, arc: 0, len: 1.4, width: 0.11,
        color: new THREE.Color(1, 0.7, 0.32).multiplyScalar(HOT * 1.2), trail: 25, lastTrail: now, head: 0.32,
        hit: () => this.shellLands(to, 1.15 + this.rnd() * 0.4),
      });
    }
    // the dust and smoke the barrage leaves over the ground it beat, rolling low and slow
    const settle = now + shells * 160 + 300;
    this.shots.push({
      from: centre.clone(), to: centre.clone(), at: settle, dur: 1, arc: 0, len: 0, width: 0,
      color: new THREE.Color(0, 0, 0), trail: 0, lastTrail: settle, head: 0,
      hit: () => {
        for (let k = 0; k < 26; k++) {
          const a = this.rnd() * Math.PI * 2, d = r * Math.sqrt(this.rnd());
          const at = centre.clone().add(new THREE.Vector3(Math.cos(a) * d, 0.2 + this.rnd() * 0.4, Math.sin(a) * d));
          this.cloudAt(at, 5000 + this.rnd() * 3000, r * 0.25, r * (0.55 + this.rnd() * 0.3), 0.5, 0.47, 0.42, 0.55, this.dir(0.2, 0.1).multiplyScalar(0.25), 0.04, 0.4);
        }
      },
    });
  }

  /** One artillery shell going off on the ground: the flash, a fireball, a tall fountain of earth and a ring through the dust. */
  private shellLands(pos: THREE.Vector3, size: number): void {
    this.glowAt(pos.clone().setY(0.4), 90, 1.4 * size, 3 * size, new THREE.Color(1, 0.96, 0.85).multiplyScalar(HOT * 1.6));
    this.fireball(pos, size, 6);
    // the earth thrown straight up in a column, brown and heavy, falling back as it spreads
    for (let k = 0; k < 14; k++) {
      const v = this.dir(0.18, 0.9).multiplyScalar((2.2 + this.rnd() * 3.2) * size);
      this.cloudAt(pos.clone().setY(0.1), 1400 + this.rnd() * 1200, 0.15 * size, (0.55 + this.rnd() * 0.5) * size, 0.36, 0.29, 0.21, 0.85, v, -0.9, 1.4);
    }
    for (let k = 0; k < 10; k++) {
      const s = (0.03 + this.rnd() * 0.05) * size;
      this.particle(this.chunks, MAX_CHUNK, {
        pos: pos.clone().setY(0.15), vel: this.dir(0.25, 0.8).multiplyScalar(4 + this.rnd() * 5), life: 2200 + this.rnd() * 800,
        size0: s, size1: s, color: new THREE.Color(0.24, 0.19, 0.14), gravity: GRAVITY * 1.4, drag: 0.5, spin: (this.rnd() - 0.5) * 20, len: s,
      });
    }
    this.ring(pos.clone().setY(0.02), 0.3 * size, 2.6 * size, 420, new THREE.Color(1, 0.86, 0.6).multiplyScalar(HOT * 0.35));
    this.ring(pos.clone().setY(0.02), 0.2 * size, 1.6 * size, 900, new THREE.Color(0.55, 0.45, 0.35).multiplyScalar(0.6));
    this.light(pos.clone().setY(1.2), 7 * size, 260, 0xffc070);
    this.shakeBy(0.12 * size);
  }

  /**
   * A round from the AC-130 landing: the 105 is a white-hot bloom with a shockwave and a long
   * shake, the 40 a sharp burst, the 25 a spit of sparks and dust.
   */
  gunshipHit(pos: THREE.Vector3, weapon: string): void {
    if (weapon === '105') {
      this.glowAt(pos.clone().setY(0.6), 160, 2, 5, new THREE.Color(1, 1, 1).multiplyScalar(HOT * 2));
      this.fireball(pos, 2.1, 10);
      this.ring(pos.clone().setY(0.03), 0.4, 5, 520, new THREE.Color(1, 0.95, 0.85).multiplyScalar(HOT * 0.5));
      this.light(pos.clone().setY(1.5), 14, 420, 0xfff0d0);
      this.shakeBy(0.45);
    } else if (weapon === '40') {
      this.glowAt(pos.clone().setY(0.3), 90, 0.8, 1.8, new THREE.Color(1, 0.98, 0.9).multiplyScalar(HOT * 1.6));
      this.fireball(pos, 0.9, 3);
      this.shakeBy(0.12);
    } else {
      this.impact(pos.clone().setY(0.1), 'heavy');
      this.glowAt(pos.clone().setY(0.15), 70, 0.35, 0.7, new THREE.Color(1, 0.95, 0.85).multiplyScalar(HOT));
    }
  }

  /** An interceptor meeting its target in the air: a flat burst of fire and a ring of smoke. */
  private airBurst(pos: THREE.Vector3): void {
    const hot = new THREE.Color(1, 0.9, 0.7).multiplyScalar(HOT * 1.4);
    this.glowAt(pos, 140, 0.5, 1.2, hot);
    for (let k = 0; k < 6; k++) this.glowAt(pos.clone().addScaledVector(this.dir(), 0.15), 350 + this.rnd() * 200, 0.3, 0.7, new THREE.Color(1, 1, 1).multiplyScalar(HOT), 'fire', this.dir().multiplyScalar(1.2));
    for (let k = 0; k < 16; k++) this.sparkAt(pos, this.dir().multiplyScalar(4 + this.rnd() * 4), 400 + this.rnd() * 400, 0.12, new THREE.Color(1, 0.75, 0.35).multiplyScalar(HOT));
    for (let k = 0; k < 8; k++) this.smokeAt(pos, 1800 + this.rnd() * 800, 0.2, 0.9, 0.55, 0.6, this.dir(0.6).multiplyScalar(0.9), 0.1);
    this.light(pos, 3, 180);
    this.shakeBy(0.08);
  }

  /** The core of every explosion: a flash, a rolling ball of fire, black smoke, sparks, chunks and a ground ring. */
  private fireball(pos: THREE.Vector3, size: number, debris = 0): void {
    const p = pos.clone();
    p.y = Math.max(p.y, 0.05);
    this.glowAt(p.clone().setY(p.y + 0.15 * size), 110, 1.1 * size, 2.2 * size, new THREE.Color(1, 0.95, 0.8).multiplyScalar(HOT * 1.3));
    const balls = Math.round(5 + size * 6);
    for (let k = 0; k < balls; k++) {
      const v = this.dir(0.3, 0.45).multiplyScalar((0.6 + this.rnd() * 1.6) * size);
      const at = p.clone().addScaledVector(this.dir(0.5, 0.3), 0.15 * size);
      this.glowAt(at, 380 + this.rnd() * 380 * size, (0.35 + this.rnd() * 0.3) * size, (0.8 + this.rnd() * 0.6) * size, new THREE.Color(1, 1, 1).multiplyScalar(HOT), 'fire', v);
    }
    // a column of black smoke that keeps rolling up after the fire is gone
    for (let k = 0; k < balls + 6; k++) {
      const v = this.dir(0.35, 0.6).multiplyScalar((0.3 + this.rnd() * 0.8) * size);
      this.smokeAt(p.clone().addScaledVector(this.dir(0.5, 0.4), 0.2 * size), 2400 + this.rnd() * 2200, 0.4 * size, (1.2 + this.rnd() * 0.9) * size, 0.12 + this.rnd() * 0.12, 1, v, 0.5);
    }
    const sparks = Math.round(10 + size * 14);
    for (let k = 0; k < sparks; k++) this.sparkAt(p, this.dir(0.1, 0.55).multiplyScalar((3 + this.rnd() * 5) * Math.sqrt(size)), 400 + this.rnd() * 600, 0.12, new THREE.Color(1, 0.65, 0.25).multiplyScalar(HOT));
    // dirt thrown up in a fan
    for (let k = 0; k < Math.round(3 + size * 4); k++) this.smokeAt(p.clone().setY(0.05), 1000 + this.rnd() * 600, 0.2 * size, 0.7 * size, 0.42, 0.7, this.dir(0.75, 0.25).multiplyScalar(1.6 * size), -0.2);
    for (let k = 0; k < debris; k++) {
      const s = (0.04 + this.rnd() * 0.07) * Math.sqrt(size);
      this.particle(this.chunks, MAX_CHUNK, {
        pos: p.clone().setY(p.y + 0.1), vel: this.dir(0.2, 0.7).multiplyScalar(3 + this.rnd() * 4), life: 2600 + this.rnd() * 800,
        size0: s, size1: s, color: new THREE.Color(0.13, 0.12, 0.11), gravity: GRAVITY * 1.4, drag: 0.6, spin: (this.rnd() - 0.5) * 22, len: s,
      });
    }
    this.ring(p.clone().setY(0), 0.2 * size, 1.8 * size, 300, new THREE.Color(1, 0.7, 0.4).multiplyScalar(HOT * 0.15));
    this.light(p.clone().setY(p.y + 0.4 * size), 4 * size, 380 + 200 * size);
  }

  /**
   * A blast at `centre`: gibs, limbs and wreckage lying within `radius` (tiles) are thrown again,
   * outward and up, harder the nearer they lie, tumbling as they go.
   */
  kick(centre: THREE.Vector3, radius: number, power: number): void {
    for (const p of this.chunks) {
      const d = Math.hypot(p.pos.x - centre.x, p.pos.z - centre.z);
      if (d > radius) continue;
      const f = power * (1 - d / radius) * (0.7 + this.rnd() * 0.6);
      const out = d > 1e-3 ? new THREE.Vector3((p.pos.x - centre.x) / d, 0, (p.pos.z - centre.z) / d) : this.dir(1);
      p.vel.addScaledVector(out, f).add(new THREE.Vector3(0, f * (0.8 + this.rnd() * 0.6), 0));
      p.spin += (this.rnd() - 0.5) * f * 8;
      // a spatter of blood stays a spatter; anything with a body to it flies again, and lives long enough to land
      if (p.wet && p.size1 > p.size0) continue;
      p.rest = false;
      p.life = Math.max(p.life, p.age + 3000);
    }
  }

  /** A brief crimson mist puff from a torn joint, along `dir`. */
  bloodSpurt(at: THREE.Vector3, dir: THREE.Vector3): void {
    this.cloudAt(at.clone(), 280 + this.rnd() * 140, 0.04, 0.13,
      0.22, 0.015, 0.015, 0.35, dir.clone().multiplyScalar(0.35), -0.08, 0.25);
  }

  /** Flares: a fan of white-hot burning decoys thrown out behind an aircraft, arcing down with smoke. */
  flares(pos: THREE.Vector3): void {
    for (let k = 0; k < 10; k++) {
      const v = this.dir(0.2, 0.1).multiplyScalar(2.5 + this.rnd() * 2);
      this.sparkAt(pos, v, 900 + this.rnd() * 500, 0.1, new THREE.Color(1, 0.85, 0.55).multiplyScalar(HOT * 1.4), 3);
      this.glowAt(pos.clone().addScaledVector(v, 0.12), 700 + this.rnd() * 300, 0.12, 0.3, new THREE.Color(1, 0.8, 0.5).multiplyScalar(HOT), 'fire', v.clone().multiplyScalar(0.6));
      this.smokeAt(pos.clone().addScaledVector(v, 0.2), 1600, 0.05, 0.35, 0.85, 0.5, v.clone().multiplyScalar(0.3).setY(-0.4), 0);
    }
    this.light(pos.clone(), 2, 500, 0xffe0a0);
  }

  /**
   * An aircraft, drone or missile destroyed in the air: a full mid-air fireball, shockwave ring,
   * billowing black smoke, sparks, and airframe wreckage tumbling down to the ground.
   */
  debris(pos: THREE.Vector3, size = 1, color?: THREE.Color): void {
    const p = pos.clone();
    p.y = Math.max(p.y, 0.45);
    this.glowAt(p, 130, 0.95 * size, 2.1 * size, new THREE.Color(1, 0.95, 0.8).multiplyScalar(HOT * 1.35));
    const balls = Math.round(5 + size * 6);
    for (let k = 0; k < balls; k++) {
      const v = this.dir(0.15, 0.25).multiplyScalar((0.6 + this.rnd() * 1.5) * size);
      const at = p.clone().addScaledVector(this.dir(0.2, 0.2), 0.14 * size);
      this.glowAt(at, 360 + this.rnd() * 340 * size, (0.32 + this.rnd() * 0.28) * size, (0.78 + this.rnd() * 0.55) * size, new THREE.Color(1, 1, 1).multiplyScalar(HOT), 'fire', v);
    }
    // secondary fuel bloom
    this.glowAt(p.clone().setY(p.y + 0.15 * size), 520, 0.32 * size, 1.35 * size, new THREE.Color(1, 0.88, 0.65).multiplyScalar(HOT), 'fire', new THREE.Vector3(0, 0.45, 0));
    for (let k = 0; k < balls + 6; k++) {
      const v = this.dir(0.25, 0.35).multiplyScalar((0.35 + this.rnd() * 0.85) * size);
      this.smokeAt(p.clone().addScaledVector(this.dir(0.3, 0.2), 0.18 * size), 2200 + this.rnd() * 2000, 0.35 * size, (1.1 + this.rnd() * 0.85) * size, 0.14 + this.rnd() * 0.14, 0.95, v, 0.35);
    }
    const hull = color ? color.clone().multiplyScalar(0.72) : null;
    const scorched = color ? color.clone().multiplyScalar(0.28) : null;
    const n = Math.round(10 + size * 8);
    for (let k = 0; k < n; k++) {
      const s = (0.035 + this.rnd() * 0.065) * Math.sqrt(size);
      const shade = 0.12 + this.rnd() * 0.22;
      const chunkColor = hull && scorched ? (this.rnd() < 0.55 ? hull : scorched) : new THREE.Color(shade, shade, shade * 0.95);
      this.particle(this.chunks, MAX_CHUNK, {
        pos: p.clone(), vel: this.dir(0.1, 0.2).multiplyScalar((1.8 + this.rnd() * 3.6) * Math.sqrt(size)), life: 4500 + this.rnd() * 2500,
        size0: s, size1: s, color: chunkColor, gravity: GRAVITY * 1.1, drag: 0.75, spin: (this.rnd() - 0.5) * 24, len: s,
        shape: this.rnd() < 0.65 ? new THREE.Vector3(1.8 + this.rnd() * 1.2, 0.35 + this.rnd() * 0.35, 0.8 + this.rnd() * 0.6) : undefined,
      });
    }
    const sparks = Math.round(12 + size * 14);
    for (let k = 0; k < sparks; k++) this.sparkAt(p, this.dir(0.05, 0.25).multiplyScalar((2.8 + this.rnd() * 4.8) * Math.sqrt(size)), 380 + this.rnd() * 480, 0.11, new THREE.Color(1, 0.7, 0.3).multiplyScalar(HOT));
    this.ring(p.clone(), 0.18 * size, 1.6 * size, 260, new THREE.Color(1, 0.75, 0.42).multiplyScalar(HOT * 0.22));
    this.light(p.clone(), 3.8 * size, 360 + 180 * size);
    this.shakeBy(0.2 * size);
  }

  /**
   * A submarine-launched missile breaking the surface: a tall column of white spray and steam,
   * the motor lighting at the top of it, and a shake felt through the ground.
   */
  seaLaunch(pos: THREE.Vector3): void {
    for (let k = 0; k < 18; k++) {
      const v = this.dir(0.6, 0.2).multiplyScalar(0.3 + this.rnd() * 0.5).add(new THREE.Vector3(0, 1.8 + this.rnd() * 2.2, 0));
      this.smokeAt(pos.clone().addScaledVector(this.dir(1), 0.15), 1600 + this.rnd() * 1400, 0.25, 1.0 + this.rnd() * 0.8, 0.92, 0.85, v, 0.15);
    }
    // the spray ring spreading across the water
    for (let k = 0; k < 12; k++) this.smokeAt(pos.clone(), 1200, 0.2, 0.7, 0.88, 0.7, this.dir(1).multiplyScalar(1.6 + this.rnd()), 0);
    this.glowAt(pos.clone().setY(1.4), 700, 0.6, 2.2, new THREE.Color(1, 0.8, 0.5).multiplyScalar(HOT), 'fire', new THREE.Vector3(0, 3, 0));
    this.light(pos.clone().setY(1.2), 6, 900);
    this.shakeBy(0.35);
  }

  /**
   * A machine that has taken damage, called every frame while it is hurt: below 60 percent it trails
   * grey smoke, below 35 black smoke and flames licking out of it, below 20 sparks as well.
   */
  /** A microwave pulse from `pos`: a pale shell racing out over `r` tiles on the ground and in the air, crackling as it goes. */
  pulse(pos: THREE.Vector3, r: number): void {
    const c = new THREE.Color(0.55, 0.9, 1).multiplyScalar(HOT);
    this.glowAt(pos, 160, 0.4, 1.4, c.clone().multiplyScalar(1.4));
    this.ring(pos.clone().setY(0.05), 0.3, r, 520, c.clone().multiplyScalar(0.45));
    this.ring(pos.clone().setY(1.2), 0.3, r * 0.9, 600, c.clone().multiplyScalar(0.3));
    for (let k = 0; k < 18; k++) this.sparkAt(pos, this.dir(0.2, 0.5).multiplyScalar(5 + this.rnd() * 6), 200 + this.rnd() * 200, 0.08, c, 0);
    this.light(pos.clone().setY(1), 5, 200, 0x8fe8ff);
  }

  /** Something set alight by incendiary rounds, called every frame while it burns. */
  burning(pos: THREE.Vector3, dt: number): void {
    // a man or a machine set alight by incendiary rounds: flames licking up off it and a thread of smoke
    if (this.rnd() < dt * 22) {
      const at = pos.clone().add(new THREE.Vector3((this.rnd() - 0.5) * 0.14, 0.08 + this.rnd() * 0.12, (this.rnd() - 0.5) * 0.14));
      this.glowAt(at, 260 + this.rnd() * 200, 0.12, 0.26, new THREE.Color(1, 1, 1).multiplyScalar(HOT), 'fire', new THREE.Vector3(0, 0.7, 0));
    }
    if (this.rnd() < dt * 6) this.smokeAt(pos.clone().setY(pos.y + 0.3), 900 + this.rnd() * 500, 0.08, 0.32, 0.2, 0.6, new THREE.Vector3(0, 0.5, 0), 0.4);
  }

  damaged(pos: THREE.Vector3, frac: number, dt: number): void {
    if (frac >= 0.6) return;
    const heavy = frac < 0.35;
    if (this.rnd() < dt * (heavy ? 16 : 7)) {
      const shade = heavy ? 0.1 + this.rnd() * 0.08 : 0.42 + this.rnd() * 0.15;
      this.smokeAt(pos.clone().addScaledVector(this.dir(0.6), 0.08), 1400 + this.rnd() * 1000, 0.08, 0.45 + this.rnd() * 0.3, shade, heavy ? 0.85 : 0.6, new THREE.Vector3(0, 0.35, 0), 0.35);
    }
    if (heavy && this.rnd() < dt * 12) {
      this.glowAt(pos.clone().addScaledVector(this.dir(0.5, 0.3), 0.07), 260 + this.rnd() * 200, 0.08, 0.22 + this.rnd() * 0.12, new THREE.Color(1, 0.55, 0.15).multiplyScalar(HOT * 0.6), 'fire', new THREE.Vector3(0, 0.5, 0));
    }
    if (frac < 0.2 && this.rnd() < dt * 6) this.sparkAt(pos, this.dir(0.3, 0.5).multiplyScalar(1.5 + this.rnd() * 2), 250, 0.05, new THREE.Color(1, 0.7, 0.3).multiplyScalar(HOT));
  }

  /** A wounded soldier, called every frame: below quarter health, an occasional faint crimson mist puff. */
  bleeding(pos: THREE.Vector3, frac: number, dt: number): void {
    if (frac >= 0.25 || this.rnd() >= dt * 1.5) return;
    this.cloudAt(pos.clone().addScaledVector(this.dir(0.5), 0.04), 260 + this.rnd() * 160, 0.04, 0.11,
      0.22, 0.015, 0.015, 0.32, new THREE.Vector3(0, 0.05, 0), -0.05, 0.2);
  }

  /**
   * A vehicle, boat or aircraft destroyed: a big fireball, and the machine itself blown into pieces
   * in its own colour (plates, wheels, a turret thrown high for a tank) that tumble, smoke and lie
   * where they land.
   */
  wreck(pos: THREE.Vector3, size = 1, color?: THREE.Color, turret = false): void {
    this.fireball(pos, size, Math.round(8 * size));
    if (color) {
      const hull = color.clone().multiplyScalar(0.7);
      const scorched = color.clone().multiplyScalar(0.3);
      const n = Math.round(8 + size * 8);
      for (let k = 0; k < n; k++) {
        const s = (0.05 + this.rnd() * 0.1) * size;
        const shape = new THREE.Vector3(1 + this.rnd() * 1.5, 0.4 + this.rnd() * 0.5, 0.7 + this.rnd() * 0.8);
        this.particle(this.chunks, MAX_CHUNK, {
          pos: pos.clone().setY(pos.y + 0.15), vel: this.dir(0.2, 0.6).multiplyScalar((2.5 + this.rnd() * 3.5) * Math.sqrt(size)),
          life: 6000 + this.rnd() * 3000, size0: s, size1: s, color: this.rnd() < 0.6 ? hull : scorched,
          gravity: GRAVITY * 1.3, drag: 0.7, spin: (this.rnd() - 0.5) * 20, len: s, shape,
        });
      }
      if (turret) {
        // the turret goes straight up, spinning, and comes down beside the hull
        const s = 0.16 * size;
        this.particle(this.chunks, MAX_CHUNK, {
          pos: pos.clone().setY(pos.y + 0.3), vel: this.dir(0.85, 0).multiplyScalar(0.8).add(new THREE.Vector3(0, 6.5, 0)),
          life: 9000, size0: s, size1: s, color: hull, gravity: GRAVITY, drag: 0.9, spin: 9, len: s, shape: new THREE.Vector3(1.4, 0.55, 1.2),
        });
      }
    }
    // a second, later bloom as the fuel goes up
    const late = pos.clone().setY(pos.y + 0.25);
    this.glowAt(late, 600, 0.3 * size, 1.4 * size, new THREE.Color(1, 0.9, 0.7).multiplyScalar(HOT), 'fire', new THREE.Vector3(0, 0.6, 0));
    this.shakeBy(0.3 * size);
  }

  /**
   * A soldier killed: a dark-crimson mist blown off the exit wound along the way the round was
   * going, a spray of droplets out of it (bright arterial red the bullet's way, a little darker
   * backspatter the shooter's way), and a wet lump or two on a heavy hit. The drops flatten into
   * a stain where they land, and the stain darkens as it dries.
   */
  flesh(pos: THREE.Vector3, blast = false, shotDir?: { x: number; y: number }): void {
    const p = pos.clone().setY(pos.y + 0.26);
    const along = (shotDir && (shotDir.x || shotDir.y)) ? new THREE.Vector3(shotDir.x, 0, shotDir.y).normalize() : this.dir(0);
    const puffs = blast ? 3 : 2;
    for (let k = 0; k < puffs; k++) {
      this.cloudAt(p.clone().addScaledVector(along, 0.05), 340 + this.rnd() * 220, 0.05, 0.17 + this.rnd() * 0.07,
        0.34, 0.02, 0.03, 0.4, along.clone().multiplyScalar(0.5 + this.rnd() * 0.4).addScaledVector(this.dir(0.6, 0.12), 0.2), -0.06, 0.3);
    }
    const drops = blast ? 7 : 5;
    for (let k = 0; k < drops; k++) {
      const back = k % 4 === 0;
      const v = along.clone().multiplyScalar(back ? -0.55 : 1)
        .addScaledVector(this.dir(0.45, back ? 0 : 0.1), back ? 0.2 : 0.5)
        .normalize().multiplyScalar(1.5 + this.rnd() * 2.2);
      this.particle(this.chunks, MAX_CHUNK, {
        pos: p.clone(), vel: v, life: 9000 + this.rnd() * 5000,
        size0: 0.008 + this.rnd() * 0.012, size1: 0,
        color: new THREE.Color(back ? 0.42 : 0.6, 0.018, 0.028).convertSRGBToLinear(),
        gravity: GRAVITY, drag: 0.04, rot: this.rnd() * 6.28, len: 0.01, wet: true,
      });
    }
    for (let k = 0; k < (blast ? 2 : 1); k++) {
      const v = along.clone().multiplyScalar(0.9 + this.rnd() * 0.8).addScaledVector(this.dir(0.3, 0.25), 0.6);
      this.particle(this.chunks, MAX_CHUNK, {
        pos: p.clone(), vel: v, life: 7000 + this.rnd() * 4000,
        size0: 0.02 + this.rnd() * 0.015, size1: 0,
        color: new THREE.Color(0.3, 0.015, 0.02).convertSRGBToLinear(),
        gravity: GRAVITY * 1.1, drag: 0.5, rot: this.rnd() * 6.28, spin: (this.rnd() - 0.5) * 12, len: 0.01, wet: true,
      });
    }
  }

  /**
   * A laser firing: the whole beam burns between the mounts for an instant and goes out; where it
   * touched, a bright hot spot flashes and a wisp of vapour lifts off. There is no round in
   * between to watch.
   */
  /**
   * A laser held on its target: a solid beam from the emitter to the target, a white-hot core in a
   * red sheath that shimmers, flaring at both ends, with the target burning where the beam touches.
   * Every fresh shot from the same emitter moves the beam on and keeps it lit a little longer.
   */
  beam(key: string, from: THREE.Vector3, to: THREE.Vector3, now: number, miss = false): void {
    const b = this.beams.get(key);
    if (b) { b.from.copy(from); b.to.copy(to); b.until = now + 280; b.miss = miss; return; }
    this.beams.set(key, { from: from.clone(), to: to.clone(), until: now + 280, lit: now, miss });
  }

  private drawBeams(now: number, dt: number): void {
    for (const [key, b] of this.beams) {
      if (now > b.until) { this.beams.delete(key); continue; }
      const d = b.from.distanceTo(b.to);
      if (d < 1e-3) continue;
      // a streak runs back from the point it is placed at: put it at the target end
      const mid = b.to;
      tmpQ.setFromUnitVectors(X_AXIS, tmpV.copy(b.to).sub(b.from).divideScalar(d));
      // fading in over the first moment it lights, and shimmering while held
      const on = Math.min(1, (now - b.lit) / 60) * (0.85 + 0.15 * Math.sin(now * 0.09 + d));
      tmpM.compose(mid, tmpQ, tmpS.set(d, 0.13, 0.13));
      this.streak.push(tmpM, tmpC.setRGB(1, 0.12, 0.08).multiplyScalar(HOT * 0.35 * on));
      tmpM.compose(mid, tmpQ, tmpS.set(d, 0.05, 0.05));
      this.streak.push(tmpM, tmpC.setRGB(1, 0.3, 0.2).multiplyScalar(HOT * 0.9 * on));
      tmpM.compose(mid, tmpQ, tmpS.set(d, 0.018, 0.018));
      this.streak.push(tmpM, tmpC.setRGB(1, 0.95, 0.9).multiplyScalar(HOT * 1.6 * on));
      // the emitter's flare, and the hot spot where it burns
      tmpM.compose(b.from, this.camQ, tmpS.setScalar(0.32 * on));
      this.glow.push(tmpM, tmpC.setRGB(1, 0.35, 0.25).multiplyScalar(HOT * on));
      if (!b.miss) {
        tmpM.compose(b.to, this.camQ, tmpS.setScalar((0.35 + 0.15 * this.rnd()) * on));
        this.glow.push(tmpM, tmpC.setRGB(1, 0.85, 0.6).multiplyScalar(HOT * 1.3 * on));
        if (this.rnd() < dt * 30) this.sparkAt(b.to, this.dir(0.3, 0.3).multiplyScalar(1 + this.rnd() * 2), 160, 0.05, new THREE.Color(1, 0.6, 0.3).multiplyScalar(HOT), GRAVITY * 0.4);
        if (this.rnd() < dt * 10) this.smokeAt(b.to, 600, 0.03, 0.18, 0.35, 0.45, new THREE.Vector3(0, 0.4, 0), 0.3);
      }
    }
  }

  laser(from: THREE.Vector3, to: THREE.Vector3, now: number, miss = false): void {
    const d = from.distanceTo(to);
    this.shots.push({
      from: from.clone(), to: to.clone(), at: now, dur: 130,
      arc: 0, len: d, width: 0.035,
      color: new THREE.Color(1, 0.22, 0.16).multiplyScalar(HOT * 0.8),
      trail: 0, lastTrail: now, head: 0,
      hit: () => {
        if (miss) return;
        this.glowAt(to, 90, 0.1, 0.16, new THREE.Color(1, 0.55, 0.3).multiplyScalar(HOT));
        this.sparkAt(to, this.dir(0.2, 0.3).multiplyScalar(0.8), 120, 0.05, new THREE.Color(1, 0.45, 0.25).multiplyScalar(HOT), 0);
        this.smokeAt(to, 500, 0.02, 0.12, 0.42, 0.4, new THREE.Vector3(0, 0.45, 0), 0.3);
        this.light(to, 0.8, 90);
      },
    });
  }



  /**
   * A nuke going off, `r` tiles across its blast: the screen whites out, a dome of light swells,
   * a ring of dust races out across the ground, and over the next seconds a fireball climbs on a
   * column of smoke and spreads into a mushroom cap. The camera shakes hard and long.
   */
  nuke(pos: THREE.Vector3, r = 3): void {
    const p = pos.clone().setY(0);
    this.white = { at: this.last, life: 1100 };
    this.glowAt(p.clone().setY(1), 500, r * 1.5, r * 4, new THREE.Color(1, 0.97, 0.88).multiplyScalar(HOT * 2));
    this.fireball(p, r * 0.8, 24);
    this.ring(p, 0.5, r * 3.4, 1100, new THREE.Color(1, 0.85, 0.6).multiplyScalar(HOT * 0.7));
    this.ring(p, 0.3, r * 2.2, 1600, new THREE.Color(1, 0.5, 0.2).multiplyScalar(HOT * 0.4));
    this.domeAt = this.last;
    this.domeR = r * 1.6;
    this.dome.position.copy(p);
    const burn = this.scorches.reduce((a, b) => (a.at < b.at ? a : b));
    burn.at = this.last;
    burn.mesh.position.copy(p).setY(0.03);
    burn.mesh.scale.setScalar(r * 1.5);
    burn.mesh.visible = true;
    this.light(p.clone().setY(r), 60, 3500, 0xffc890);
    this.trauma = 1;
    this.quake = { at: this.last, life: 4500, amp: 1 };
    if (this.nukes.length >= 3) this.nukes.shift();
    this.nukes.push({ pos: p, at: this.last, r, acc: {} });
  }

  /** How white the whole screen should be just now, 0 to 1 (the scene lays a white sheet over the view). */
  whiteout(now: number): number {
    const t = (now - this.white.at) / this.white.life;
    if (t < 0 || t >= 1) return 0;
    return t < 0.06 ? 1 : Math.pow(1 - (t - 0.06) / 0.94, 2);
  }

  /**
   * Marks a nuke or EMP called in on the ground, called every frame while it counts down: a red
   * (or blue) ring the size of the blast, a ring that shrinks to the middle as the time runs out,
   * a pulsing dot that beats faster near the end, and for a nuke, the warhead falling onto it
   * (unless it is carried by a unit drawn on its own, a submarine's ICBM).
   */
  marker(key: string, pos: THREE.Vector3, radius: number, kind: IncomingStrike['kind'], secondsLeft: number, carried = false): void {
    let m = this.markers.get(key);
    if (!m) {
      const mat = () => new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
      const group = new THREE.Group();
      const ring = new THREE.Mesh(this.markerGeo.ring, mat());
      const count = new THREE.Mesh(this.markerGeo.count, mat());
      const dot = new THREE.Mesh(this.markerGeo.dot, mat());
      const ticks = new THREE.Mesh(this.markerGeo.ticks, mat());
      group.add(ring, count, dot, ticks);
      for (const o of group.children) o.renderOrder = 2;
      this.group.add(group);
      m = { group, ring, count, dot, ticks, kind, pos: pos.clone(), radius, total: Math.max(0.5, secondsLeft), landAt: this.last + secondsLeft * 1000, seen: this.last, carried };
      this.markers.set(key, m);
    }
    m.seen = this.last;
    m.pos.copy(pos);
    m.radius = radius;
    // the sim counts in whole ticks; ease towards its estimate so the fall is smooth
    m.landAt += (this.last + secondsLeft * 1000 - m.landAt) * 0.1;
  }

  /** Draws every marker seen this frame and drops the ones that have gone (landed, or the raid ended). */
  private updateMarkers(now: number): void {
    for (const [key, m] of this.markers) {
      if (now - m.seen > 250) {
        this.group.remove(m.group);
        for (const o of m.group.children) ((o as THREE.Mesh).material as THREE.Material).dispose();
        this.markers.delete(key);
        continue;
      }
      const left = Math.max(0, (m.landAt - now) / 1000);
      const nuke = m.kind === 'nuke';
      // the enemy's nuke red and EMP blue; the defender's own fire mission amber
      const base = nuke ? new THREE.Color(1, 0.12, 0.08) : m.kind === 'artillery' ? new THREE.Color(1, 0.66, 0.2) : new THREE.Color(0.35, 0.85, 1);
      const urgency = 1 + Math.max(0, 5 - left) * 0.9;
      const beat = 0.5 + 0.5 * Math.sin((now / 1000) * Math.PI * 2 * urgency);
      m.group.position.copy(m.pos).setY(0.04);
      m.ring.scale.setScalar(m.radius);
      (m.ring.material as THREE.MeshBasicMaterial).color.copy(base).multiplyScalar(HOT * (0.25 + 0.25 * beat));
      const frac = Math.min(1, left / m.total);
      m.count.scale.setScalar(Math.max(0.05, m.radius * frac));
      (m.count.material as THREE.MeshBasicMaterial).color.copy(base).multiplyScalar(HOT * 0.35);
      m.dot.scale.setScalar(0.12 + 0.05 * beat);
      (m.dot.material as THREE.MeshBasicMaterial).color.copy(base).multiplyScalar(HOT * (0.8 + 0.6 * beat));
      m.ticks.scale.setScalar(m.radius);
      m.ticks.rotation.y = now / 1400;
      (m.ticks.material as THREE.MeshBasicMaterial).color.copy(base).multiplyScalar(HOT * 0.4);
      // the dot glows up off the ground
      tmpM.compose(tmpV.copy(m.pos).setY(0.08), this.camQ, tmpS.setScalar(0.35 + 0.3 * beat));
      this.glow.push(tmpM, tmpC.copy(base).multiplyScalar(HOT * (0.5 + 0.5 * beat)));
      if (nuke && !m.carried && left < NUKE_FALL) {
        // the warhead drops nose-first onto the dot, faster and faster, on a bright trail
        const k = left / NUKE_FALL;
        const at = m.pos.clone().setY(0.3 + 26 * k * k);
        if (!m.warhead) m.warhead = at.clone().setY(at.y + 1);
        const tail = at.clone().setY(at.y + 0.5);
        // a short-lived streak: it should not stand as a pole through the cloud afterwards
        this.trailPuffs(m.warhead, tail, 1.6, 0.3);
        tmpQ.setFromUnitVectors(X_AXIS, tmpV.set(0, -1, 0));
        tmpM.compose(at, tmpQ, tmpS.setScalar(3.2));
        this.body.push(tmpM, tmpC.setRGB(0.45, 0.45, 0.4));
        tmpM.compose(tail, this.camQ, tmpS.setScalar(0.5 + 0.2 * this.rnd()));
        this.glow.push(tmpM, tmpC.setRGB(1, 0.75, 0.4).multiplyScalar(HOT));
      }
    }
  }

  /** Builds each nuke's cloud a little more every frame: the climbing fireball, the stem, the cap and the dust ring. */
  private updateNukes(now: number, dt: number): void {
    const spawn = (n: Nuke, what: string, rate: number) => {
      // whole puffs for this frame at `rate` a second, carrying the fraction over
      const want = (n.acc[what] ?? 0) + rate * dt;
      const k = Math.floor(want);
      n.acc[what] = want - k;
      return k;
    };
    this.nukes = this.nukes.filter(n => {
      const T = (now - n.at) / 1000;
      if (T > 5) return false;
      const r = n.r, H = r * 2.4;
      // the cap climbs fast, then slows as it spreads
      const h = H * (1 - Math.exp(-T * 0.9));
      const capR = r * (0.4 + 0.85 * Math.min(1, T / 2.5));
      const p = n.pos;
      if (T < 2.6) {
        for (let i = spawn(n, 'fire', 45); i > 0; i--) {
          const at = p.clone().add(this.dir(0.4).multiplyScalar(r * 0.3)).setY(Math.max(0.3, h * 0.92 + (this.rnd() - 0.5) * r * 0.3));
          this.glowAt(at, 500 + this.rnd() * 300, r * 0.6, r * 1.1, new THREE.Color(1, 1, 1).multiplyScalar(HOT * (1 - T / 2.6)), 'fire', new THREE.Vector3(0, 0.3, 0));
        }
      }
      if (T < 4) {
        for (let i = spawn(n, 'stem', 50); i > 0; i--) {
          const y = this.rnd() * h * 0.9;
          const w = r * 0.22 * (1 - (y / H) * 0.4);
          const a = this.rnd() * Math.PI * 2;
          const at = p.clone().add(new THREE.Vector3(Math.cos(a) * w, y, Math.sin(a) * w));
          const glow = Math.max(0, 1 - T / 1.6);
          this.cloudAt(at, 3800 + this.rnd() * 1800, r * 0.3, r * (0.55 + this.rnd() * 0.3), 0.42 + 0.4 * glow, 0.36 + 0.18 * glow, 0.3, 0.9, new THREE.Vector3(0, r * 0.35, 0), 0.05);
        }
      }
      if (T < 4.5) {
        for (let i = spawn(n, 'cap', 70); i > 0; i--) {
          const a = this.rnd() * Math.PI * 2, b = this.rnd() * Math.PI * 2;
          const roll = r * 0.35;
          const out = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
          // a flattened doughnut, so the cap spreads wide rather than standing as a ball
          const at = p.clone().addScaledVector(out, this.rnd() * capR * 0.6 + capR * 0.5 + Math.cos(b) * roll).setY(h + Math.sin(b) * roll * 0.6);
          // the cap rolls outward over its top and down its rim, glowing from inside at first
          const glow = Math.max(0, 1 - T / 1.8);
          const vel = out.clone().multiplyScalar(r * 0.25).add(new THREE.Vector3(0, r * 0.1 * Math.sin(b), 0));
          this.cloudAt(at, 4500 + this.rnd() * 2500, r * 0.45, r * (0.9 + this.rnd() * 0.5), 0.5 + 0.45 * glow, 0.42 + 0.2 * glow, 0.36 + 0.02 * glow, 0.92, vel, 0.02);
        }
      }
      if (T < 1.5) {
        // dust thrown up by the shockwave racing out over the ground
        const rs = r * 3.2 * (1 - Math.exp(-T * 2.8));
        for (let i = spawn(n, 'dust', 220); i > 0; i--) {
          const a = this.rnd() * Math.PI * 2;
          const out = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
          this.cloudAt(p.clone().addScaledVector(out, rs * (0.85 + this.rnd() * 0.2)).setY(0.15), 2600 + this.rnd() * 1400, r * 0.3, r * (0.65 + this.rnd() * 0.35), 0.6, 0.52, 0.42, 0.6, out.multiplyScalar(r * 0.9 * (1 - T / 1.5)).setY(0.3), 0.05, 0.3);
        }
      }
      return true;
    });
    const dt2 = (now - this.domeAt) / 700;
    this.dome.visible = dt2 >= 0 && dt2 < 1;
    if (this.dome.visible) {
      this.dome.scale.setScalar(this.domeR * (0.2 + 1.3 * (1 - (1 - dt2) * (1 - dt2))));
      (this.dome.material as THREE.MeshBasicMaterial).color.setRGB(1, 0.95, 0.85).multiplyScalar(HOT * 0.15 * (1 - dt2) * (1 - dt2));
    }
    for (const s of this.scorches) {
      // the burn stays a long while, then fades
      const age = (now - s.at) / 1000;
      s.mesh.visible = age >= 0 && age < 40;
      if (s.mesh.visible) (s.mesh.material as THREE.MeshBasicMaterial).opacity = Math.min(1, age * 4) * (age < 25 ? 1 : 1 - (age - 25) / 15);
    }
  }

  /** An EMP: a blue shell of light rolling out and crackling sparks. */
  emp(pos: THREE.Vector3): void {
    const blue = new THREE.Color(0.45, 0.9, 1).multiplyScalar(HOT);
    this.glowAt(pos.clone().setY(0.6), 300, 1, 5, blue);
    this.ring(pos.clone().setY(0), 0.3, 6, 700, blue);
    this.ring(pos.clone().setY(0), 0.2, 4, 1000, blue.clone().multiplyScalar(0.5));
    for (let k = 0; k < 40; k++) this.sparkAt(pos.clone().setY(0.4), this.dir(0.6, 0.2).multiplyScalar(4 + this.rnd() * 6), 300 + this.rnd() * 500, 0.18, blue, 0);
    this.light(pos.clone().setY(1.5), 12, 700, 0x6fe3ff);
    this.shakeBy(0.4);
  }

  /** Moves everything on and refills the instanced pools. `camera` turns the billboards to face it. */
  update(now: number, camera: THREE.Camera): void {
    const dt = Math.min(0.05, Math.max(0, (now - (this.last || now)) / 1000));
    this.last = now;
    this.trauma = Math.max(0, this.trauma - dt * 1.4);
    const face = camera.quaternion;
    this.camQ.copy(face);
    const right = this.camRight.set(1, 0, 0).applyQuaternion(face);
    const up = this.camUp.set(0, 1, 0).applyQuaternion(face);
    const fwd = this.camFwd.set(0, 0, 1).applyQuaternion(face);
    for (const [k, e] of this.exhausts) if (now - e.seen > 500) this.exhausts.delete(k);
    this.updateNukes(now, dt);

    // shots in flight
    for (let i = this.shots.length - 1; i >= 0; i--) {
      const s = this.shots[i]!;
      const t = (now - s.at) / s.dur;
      if (t >= 1) {
        this.shots.splice(i, 1);
        s.hit();
        continue;
      }
      if (t < 0) continue;
      const pos = this.along(s, t, new THREE.Vector3());
      const back = this.along(s, Math.max(0, t - 0.02), new THREE.Vector3());
      const dir = pos.clone().sub(back);
      if (dir.lengthSq() < 1e-8) dir.copy(s.to).sub(s.from);
      dir.normalize();
      if (s.missile) {
        // the trail comes off the nozzle, a little behind the body's middle
        this.trailPuffs(s.trailAt!, pos.clone().addScaledVector(dir, -0.2));
      }
      if (s.trail && now - s.lastTrail > s.trail) {
        s.lastTrail = now;
        this.smokeAt(pos, 900 + this.rnd() * 600, 0.07, 0.38, 0.8, 0.5, this.dir().multiplyScalar(0.15), 0.15);
        if (s.head > 0.2) this.glowAt(pos, 120, 0.16, 0.05, new THREE.Color(1, 0.6, 0.2).multiplyScalar(HOT * 0.7));
      }
      (s as Projectile & { cur?: [THREE.Vector3, THREE.Vector3] }).cur = [pos, dir];
    }

    this.step(this.glows, dt);
    this.step(this.smokes, dt);
    this.step(this.sparks, dt);
    this.stepChunks(dt);

    this.glow.begin();
    this.body.begin();
    this.updateMarkers(now);
    for (const p of this.glows) {
      const t = p.age / p.life;
      const size = p.size0 + (p.size1 - p.size0) * (1 - (1 - t) * (1 - t));
      if (p.ramp === 'fire') {
        // white-hot, then orange, then a dull red that fades out into the smoke
        if (t < 0.25) tmpC.copy(FIRE_HOT).lerp(FIRE_MID, t / 0.25);
        else tmpC.copy(FIRE_MID).lerp(FIRE_COOL, (t - 0.25) / 0.75);
        tmpC.multiplyScalar(HOT * 0.55 * (1 - t) * (1 - t) + 0.02);
      } else tmpC.copy(p.color).multiplyScalar((1 - t) * (1 - t));
      this.glow.pushCard(p.pos, size, p.rot, right, up, fwd, tmpC);
    }
    // (the glow pool stays open: missile heads are added to it with the streaks below)

    this.smoke.begin();
    for (const p of this.smokes) {
      const t = p.age / p.life;
      const size = p.size0 + (p.size1 - p.size0) * Math.sqrt(t);
      // swells in quickly, thins out slowly
      const a = p.alpha * Math.min(1, t * 6) * Math.pow(1 - t, 1.3);
      this.smoke.pushCard(p.pos, size, p.rot, right, up, fwd, p.color, a);
    }
    this.smoke.end();

    this.streak.begin();
    for (const s of this.shots) {
      const cur = (s as Projectile & { cur?: [THREE.Vector3, THREE.Vector3] }).cur;
      if (!cur) continue;
      const [pos, dir] = cur;
      if (s.missile) {
        tmpQ.setFromUnitVectors(X_AXIS, dir);
        tmpM.compose(pos, tmpQ, tmpS.setScalar(1));
        this.body.push(tmpM, tmpC.setRGB(1, 1, 1));
        // the flame: a short hot tongue out of the tail and a flickering glow round the nozzle
        const tail = tmpV.copy(pos).addScaledVector(dir, -0.17).clone();
        const flick = 0.75 + 0.5 * this.rnd();
        tmpQ.setFromUnitVectors(X_AXIS, tmpV.copy(dir).negate());
        tmpM.compose(tail, tmpQ, tmpS.set(0.16 * flick, 0.035, 0.035));
        this.streak.push(tmpM, tmpC.setRGB(1, 0.75, 0.4).multiplyScalar(HOT));
        tmpM.compose(tail, face, tmpS.setScalar(0.2 * flick));
        this.glow.push(tmpM, tmpC.setRGB(1, 0.65, 0.3).multiplyScalar(HOT));
        continue;
      }
      // never longer than the way it has come: outer coloured tracer sheath + white-hot inner core
      const len = Math.min(s.len, s.from.distanceTo(pos) + 0.05);
      tmpQ.setFromUnitVectors(X_AXIS, dir);
      tmpM.compose(pos, tmpQ, tmpS.set(len, s.width, s.width));
      this.streak.push(tmpM, s.color);
      tmpM.compose(pos, tmpQ, tmpS.set(len * 0.68, s.width * 0.45, s.width * 0.45));
      this.streak.push(tmpM, tmpC.setRGB(1, 0.96, 0.88).multiplyScalar(HOT * 1.35));
      if (s.head > 0) {
        tmpM.compose(pos, face, tmpS.setScalar(s.head));
        this.glow.push(tmpM, s.color);
      }
    }
    for (const p of this.sparks) {
      const t = p.age / p.life;
      const speed = p.vel.length();
      if (speed < 1e-4) continue;
      tmpQ.setFromUnitVectors(X_AXIS, tmpV.copy(p.vel).divideScalar(speed));
      const w = p.size0 + (p.size1 - p.size0) * t;
      tmpM.compose(p.pos, tmpQ, tmpS.set(p.len * Math.min(2.5, 0.4 + speed * 0.12), w, w));
      tmpC.copy(p.color).multiplyScalar(1 - t * t);
      this.streak.push(tmpM, tmpC);
    }
    this.drawBeams(now, dt);
    this.streak.end();
    this.glow.end();
    this.body.end();

    this.chunk.begin();
    this.drop.begin();
    this.gib.begin();
    this.gut.begin();
    for (const p of this.chunks) {
      tmpQ.setFromAxisAngle(tmpV.set(Math.sin(p.rot), Math.cos(p.rot * 0.7), 0.3).normalize(), p.rot * 3);
      // the last moment, sink into the ground
      const t = p.age / p.life;
      const sink = t > 0.85 ? (t - 0.85) / 0.15 * p.size0 * 1.5 : 0;
      if (p.wet && p.rest && p.size1 > p.size0) {
        // a drop that has landed: a small stain flat on the ground, darkening as it dries into the dust
        tmpQ.setFromAxisAngle(tmpV.set(0, 1, 0), p.rot);
        tmpM.compose(tmpV.copy(p.pos).setY(0.01), tmpQ, tmpS.set(p.size1, 0.005, p.size1 * (0.8 + (p.rot % 1) * 0.4)));
        tmpC.copy(p.color).multiplyScalar(1 - 0.55 * Math.min(1, t * 1.5));
        this.drop.push(tmpM, tmpC);
      } else if (p.wet && !p.shape && p.size0 < 0.03) {
        // a drop in flight: round, drawn out a little along the way it is going
        tmpM.compose(tmpV.copy(p.pos), tmpQ.identity(), tmpS.set(p.size0, p.size0 * 1.4, p.size0));
        this.drop.push(tmpM, p.color);
      } else if (p.wet && p.shape) {
        // a length of gut, tumbling, lying along the ground once it lands
        if (p.rest) tmpQ.setFromAxisAngle(tmpV.set(0, 0, 1), Math.PI / 2).premultiply(tmpQ2.setFromAxisAngle(tmpV.set(0, 1, 0), p.rot));
        tmpM.compose(tmpV.copy(p.pos).setY(p.pos.y - sink), tmpQ, tmpS.set(p.size0 * p.shape.y, p.size0 * p.shape.x, p.size0 * p.shape.z));
        this.gut.push(tmpM, p.color);
      } else if (p.wet) {
        // a lump of flesh
        tmpM.compose(tmpV.copy(p.pos).setY(p.pos.y - sink), tmpQ, tmpS.set(p.size0, p.size0 * 0.8, p.size0 * 0.9));
        this.gib.push(tmpM, p.color);
      } else if (p.shape) {
        if (p.rest) tmpQ.setFromAxisAngle(tmpV.set(0, 1, 0), p.rot);
        tmpM.compose(tmpV.copy(p.pos).setY(p.pos.y - sink), tmpQ, tmpS.copy(p.shape).multiplyScalar(p.size0));
        this.chunk.push(tmpM, p.color);
      } else {
        tmpM.compose(tmpV.copy(p.pos).setY(p.pos.y - sink), tmpQ, tmpS.set(p.size0 * 1.4, p.size0, p.size0 * 1.1));
        this.chunk.push(tmpM, p.color);
      }
    }
    this.chunk.end();
    this.drop.end();
    this.gib.end();
    this.gut.end();

    for (let i = this.rings.length - 1; i >= 0; i--) {
      const r = this.rings[i]!;
      const t = (now - r.at) / r.life;
      if (t >= 1) {
        r.mesh.visible = false;
        this.rings.splice(i, 1);
        continue;
      }
      const e = 1 - (1 - t) * (1 - t) * (1 - t);
      r.mesh.scale.setScalar(r.r0 + (r.r1 - r.r0) * e);
      (r.mesh.material as THREE.MeshBasicMaterial).color.copy(r.color).multiplyScalar(Math.pow(1 - t, 3));
    }

    // the brightest flashes still burning get the lights
    this.flashes = this.flashes.filter(f => now - f.at < f.life);
    const lit = this.flashes
      .map(f => ({ f, k: f.power * (1 - (now - f.at) / f.life) }))
      .sort((a, b) => b.k - a.k);
    for (let i = 0; i < LIGHTS; i++) {
      const l = this.lights[i]!;
      const x = lit[i];
      if (!x) { l.intensity = 0; continue; }
      l.position.copy(x.f.pos);
      l.color.copy(x.f.color);
      l.intensity = x.k * 2.2;
      l.distance = 2 + Math.sqrt(x.f.power) * 2.2;
    }
  }

  /** Where a shot is at `t` of its flight: along the line, lifted on a parabola by its arc. */
  private along(s: Projectile, t: number, out: THREE.Vector3): THREE.Vector3 {
    // a missile leaves slowly and speeds up on its motor
    if (s.missile) t = t * (0.3 + 0.7 * t);
    out.copy(s.from).lerp(s.to, t);
    out.y += s.arc * 4 * t * (1 - t);
    return out;
  }

  private step(list: Particle[], dt: number): void {
    let w = 0;
    for (const p of list) {
      p.age += dt * 1000;
      if (p.age >= p.life) continue;
      p.vel.multiplyScalar(Math.pow(p.drag, dt));
      p.vel.y += (p.buoy - p.gravity) * dt;
      p.pos.addScaledVector(p.vel, dt);
      if (p.pos.y < 0.01 && p.gravity > 0) {
        // sparks skip off the ground
        p.pos.y = 0.01;
        p.vel.y = Math.abs(p.vel.y) * 0.35;
        p.vel.x *= 0.6;
        p.vel.z *= 0.6;
      }
      p.rot += p.spin * dt;
      list[w++] = p;
    }
    list.length = w;
  }

  private stepChunks(dt: number): void {
    let w = 0;
    for (const p of this.chunks) {
      p.age += dt * 1000;
      if (p.age >= p.life) continue;
      if (!p.rest) {
        p.vel.y -= p.gravity * dt;
        p.pos.addScaledVector(p.vel, dt);
        p.rot += p.spin * dt;
        if (p.pos.y < p.size0 * 0.5) {
          p.pos.y = p.size0 * 0.5;
          // blood and flesh do not bounce: they land with a slap and stay, a droplet flattening into a spatter
          if (p.wet) { p.rest = true; p.vel.set(0, 0, 0); if (p.size0 < 0.03) { p.size1 = p.size0 * 2.4; } this.chunks[w++] = p; continue; }
          p.vel.y = -p.vel.y * 0.3;
          p.vel.x *= 0.5;
          p.vel.z *= 0.5;
          p.spin *= 0.5;
          if (p.vel.length() < 0.4) p.rest = true;
        }
        // a hot chunk smokes as it flies
        if (!p.rest && !p.wet && !p.brass && this.rnd() < dt * 18) this.smokeAt(p.pos, 500, 0.04, 0.2, 0.2, 0.5, new THREE.Vector3(), 0.3);
      }
      this.chunks[w++] = p;
    }
    this.chunks.length = w;
  }

  /** Drops everything in flight, e.g. when a raid ends. */
  clear(): void {
    this.glows = []; this.smokes = []; this.sparks = []; this.chunks = []; this.shots = []; this.flashes = []; this.nukes = [];
    this.exhausts.clear();
    for (const s of this.scorches) { s.at = -Infinity; s.mesh.visible = false; }
    for (const r of this.rings) r.mesh.visible = false;
    this.rings = [];
    this.trauma = 0;
  }
}
